import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TenantContext } from "../../contexts/TenantContext";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Grid,
  Stack,
  Tooltip,
  TextField,
  MenuItem,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import BuildIcon from "@mui/icons-material/Build";
import {
  fetchChecklist,
  fetchChecklistEvaluation,
  saveEvaluationDraft,
  submitInstructorSignOff,
  reEvaluateChecklist,
  fetchEvaluationAttempts,
  fetchRegistrationsForClass,
  fetchUsersByIds,
  fetchChecklistProgress,
  fetchPracticeAttempts,
} from "../../api/backend";
import { T, itemPoints } from "../../components/checklistScoring";
import PracticeHistoryPanel from "../../components/PracticeHistoryPanel";
import ChecklistAssessmentScreen from "../../components/ChecklistAssessmentScreen";

const MOBILE_ASSESSMENT_FOOTER_CLEARANCE = "calc(20rem + env(safe-area-inset-bottom))";

// ─── Bottom Bar ───────────────────────────────────────────────────────────────

function BottomBar({
  scoredCount,
  totalCount,
  earned,
  total,
  status,
  criticalsBlocked,
  missedWithoutNote,
  canSubmit,
  override,
  setOverride,
  prereqMet,
  saving,
  submitting,
  onSave,
  onSubmit,
  isLocked,
}) {
  if (isLocked) return null;

  return (
    <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 40 }}>
      {/* Critical info banner — informational only, does not block submit */}
      {criticalsBlocked && (
        <Box sx={{ bgcolor: "var(--color-warn-bg)", borderTop: `1px solid #FED7AA`, px: 2.5, py: 0.875, display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 15, color: "var(--color-warn)" }} />
          <Typography sx={{ fontSize: "0.78rem", color: "var(--color-warn)", fontWeight: 600, flex: 1 }}>
            Critical criterion missed: automatic result is Fail.
          </Typography>
        </Box>
      )}
      {/* Prereq warning */}
      {!prereqMet && !override && (
        <Box sx={{ bgcolor: "var(--color-warn-bg)", borderTop: `1px solid var(--color-warn-bg)`, px: 2.5, py: 0.875, display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 15, color: "var(--color-warn)" }} />
          <Typography sx={{ fontSize: "0.78rem", color: "var(--color-warn)", fontWeight: 600, flex: 1 }}>
            Peer prerequisite not yet met — the student needs a passing practice attempt with a partner, or you can override.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "none", py: 0.25, borderColor: "var(--color-warn)", color: "var(--color-warn)", "&:hover": { borderColor: "var(--color-warn)", bgcolor: "var(--color-warn-bg)" } }}
            onClick={() => setOverride(true)}
          >
            Override
          </Button>
        </Box>
      )}
      {override && !prereqMet && (
        <Box sx={{ bgcolor: "var(--color-warn-bg)", borderTop: `1px solid var(--color-warn-bg)`, px: 2.5, py: 0.5 }}>
          <Typography sx={{ fontSize: "0.72rem", color: "var(--color-warn)", fontWeight: 600 }}>
            Override active — prerequisite requirement bypassed.
          </Typography>
        </Box>
      )}
      {/* Missing notes warning — hard blocker */}
      {missedWithoutNote > 0 && (
        <Box sx={{ bgcolor: "var(--color-danger-bg)", borderTop: `2px solid ${T.missed}`, px: 2.5, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: T.missed, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.82rem", color: "#7F1D1D", fontWeight: 700, flex: 1 }}>
            {missedWithoutNote} missed item{missedWithoutNote > 1 ? "s" : ""} — scroll up and add an instructor note for each missed item before signing off.
          </Typography>
        </Box>
      )}

      {/* Main action row */}
      <Box
        sx={{
          bgcolor: "white",
          borderTop: `1px solid ${T.border}`,
          px: { xs: 2, sm: 4 },
          py: { xs: 1.25, sm: 1.5 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1.25, sm: 2 },
          boxShadow: "0 -1px 6px rgba(0,0,0,0.05)",
        }}
      >
        {/* Stats */}
        <Box sx={{ display: "flex", gap: 2.5, flex: 1 }}>
          <Box>
            <Typography
              sx={{
                fontSize: "0.6rem",
                fontWeight: 700,
                color: T.na,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Scored
            </Typography>
            <Typography
              sx={{ fontSize: "0.875rem", fontWeight: 700, color: T.ink, fontFamily: "var(--font-display)" }}
            >
              {scoredCount}/{totalCount}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.6rem",
                fontWeight: 700,
                color: T.na,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Points
            </Typography>
            <Typography
              sx={{ fontSize: "0.875rem", fontWeight: 700, color: T.ink, fontFamily: "var(--font-display)" }}
            >
              {earned}/{total}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.6rem",
                fontWeight: 700,
                color: T.na,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Result
            </Typography>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 800,
                color:
                  status === "pass"
                    ? T.performed
                    : status === "fail"
                      ? T.missed
                      : T.na,
                fontFamily: "var(--font-display)",
              }}
            >
              {status === "pass" ? "PASS" : status === "fail" ? "FAIL" : "—"}
            </Typography>
          </Box>
        </Box>

        {/* Buttons */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={saving || submitting}
            fullWidth={false}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              whiteSpace: "nowrap",
              borderColor: T.primary,
              color: T.primary,
              "&:hover": { borderColor: T.primary, bgcolor: `${T.primary}0A` },
            }}
          >
            {saving ? "Saving…" : "Save Draft"}
          </Button>
          <Tooltip
            title={
              !canSubmit && missedWithoutNote > 0
                ? `Add notes for ${missedWithoutNote} missed item${missedWithoutNote > 1 ? "s" : ""} to enable sign-off`
                : !canSubmit && scoredCount < totalCount
                ? `Score all ${totalCount - scoredCount} remaining item${totalCount - scoredCount > 1 ? "s" : ""} first`
                : ""
            }
            arrow
          >
            <span style={{ display: "flex" }}>
              <Button
                variant="contained"
                startIcon={<HowToRegIcon />}
                onClick={onSubmit}
                disabled={saving || submitting || !canSubmit}
                fullWidth={false}
                sx={{
                  flex: 1,
                  bgcolor: canSubmit ? "#C0392B" : undefined,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "#922B21" },
                  "&.Mui-disabled": { bgcolor: "var(--color-hairline-cloud)", color: "white" },
                }}
              >
                {submitting ? "Submitting…" : "Submit & Sign Off"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

// Display name for a user record: fullName, else first+last, else email.
const personName = (u) =>
  u?.fullName || `${u?.firstName ?? ""} ${u?.lastName ?? ""}`.trim() || u?.email || "";

// ─── Student Selector ─────────────────────────────────────────────────────────

function StudentSelector({ courseId, checklistId }) {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [progressData, setProgressData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchChecklist(checklistId),
      fetchRegistrationsForClass(courseId),
      fetchChecklistProgress(courseId),
    ])
      .then(([cl, regs, progress]) => {
        setChecklist(cl);
        const progressMap = {};
        if (progress?.students) {
          progress.students.forEach((sp) => {
            progressMap[sp.studentId] = sp;
          });
        }
        setProgressData(progressMap);
        const ids = (regs || []).map((r) => r.userId).filter(Boolean);
        if (ids.length === 0) {
          setStudents([]);
          setLoading(false);
          return;
        }
        return fetchUsersByIds(ids).then((users) => {
          setStudents(Array.isArray(users) ? users : []);
          setLoading(false);
        });
      })
      .catch(() => {
        setError("Could not load enrolled students.");
        setLoading(false);
      });
  }, [courseId, checklistId]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  if (error) return <Alert severity="warning">{error}</Alert>;

  // progressData[studentId] is a course-wide aggregate spanning every skill sheet
  // in the course — never read its top-level completed/passed/failed fields here,
  // they'd bleed a student's history on OTHER checklists into this one. Only the
  // per-checklist entry (matched by the current checklistId) is valid for this view.
  const checklistIdNum = Number(checklistId);
  const statusForStudent = (studentId) =>
    progressData[studentId]?.checklists?.find(
      (c) => c.checklistId === checklistIdNum,
    ) ?? null;

  const total = students.length;
  const withEval = students.filter(
    (s) => statusForStudent(s.id)?.status && statusForStudent(s.id).status !== "NOT_STARTED",
  ).length;
  let passing = 0,
    failing = 0;
  students.forEach((s) => {
    const cs = statusForStudent(s.id);
    if (cs?.status === "PASS") passing++;
    if (cs?.status === "FAIL") failing++;
  });

  return (
    <Box>
      {checklist && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            border: `1px solid ${T.border}`,
            bgcolor: "var(--color-success-bg)",
            borderColor: "var(--color-success-bg)",
            p: 2.5,
            mb: 3,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color={T.primary}
            sx={{ mb: 1.5, fontFamily: "var(--font-display)" }}
          >
            Assessment Progress: {checklist.title}
          </Typography>
          <Grid container spacing={2}>
            {[
              { label: "Total Students", value: total, color: T.primary },
              {
                label: "Pending",
                value: total - withEval,
                color: "var(--color-warn)",
                icon: (
                  <HourglassBottomIcon
                    sx={{ fontSize: 15, color: "var(--color-warn)" }}
                  />
                ),
              },
              {
                label: "Passing",
                value: passing,
                color: T.performed,
                icon: (
                  <CheckCircleIcon sx={{ fontSize: 15, color: T.performed }} />
                ),
              },
              {
                label: "Failing",
                value: failing,
                color: T.missed,
                icon: <CancelIcon sx={{ fontSize: 15, color: T.missed }} />,
              },
            ].map(({ label, value, color, icon }) => (
              <Grid item xs={6} sm={3} key={label}>
                <Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                  >
                    {label}
                  </Typography>
                  <Box
                    sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}
                  >
                    {icon}
                    <Typography variant="h6" fontWeight={800} color={color} sx={{ fontFamily: "var(--font-display)" }}>
                      {value}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Paper
        elevation={0}
        sx={{ borderRadius: "12px", border: `1px solid ${T.border}` }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} color={T.primary} sx={{ fontFamily: "var(--font-display)" }}>
            Select a Student to Assess
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {students.length} enrolled student{students.length !== 1 ? "s" : ""}
          </Typography>
        </Box>

        {students.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <PersonSearchIcon
              sx={{ fontSize: 48, color: "var(--color-hairline-cloud)", mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              No enrolled students found.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {students.map((student, idx) => {
              const initials =
                personName(student).split(/\s+/).map((n) => n[0]).join("").toUpperCase().slice(0, 2) ||
                "?";
              const cs = statusForStudent(student.id);
              const isCompleted = cs?.status === "PASS" || cs?.status === "FAIL";
              const isPassed = cs?.status === "PASS";
              const isFailed = cs?.status === "FAIL";
              const pts = isCompleted ? cs.pointsAwarded ?? 0 : null;
              const maxPts = cs?.totalPoints ?? (checklist?.items?.length || 0);

              return (
                <React.Fragment key={student.id}>
                  {idx > 0 && <Divider component="li" />}
                  <ListItem
                    button
                    sx={{
                      px: 2.5,
                      py: 2,
                      "&:hover": { bgcolor: T.surface2, cursor: "pointer" },
                    }}
                    onClick={() =>
                      navigate(
                        `/course-management/${courseId}/checklists/${checklistId}/assess/${student.id}`,
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: T.primary,
                          width: 36,
                          height: 36,
                          fontSize: "0.85rem",
                        }}
                      >
                        {initials}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight={600}>
                          {personName(student) || `ID: ${student.id}`}
                        </Typography>
                      }
                      secondary={student.email || `ID: ${student.id}`}
                    />
                    {isCompleted && pts !== null && (
                      <Chip
                        label={`${pts}/${maxPts} pts`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          bgcolor: isPassed ? "var(--color-success-bg)" : "var(--color-danger-bg)",
                          color: isPassed ? T.performed : T.missed,
                          mr: 1,
                        }}
                      />
                    )}
                    {isCompleted && isPassed && (
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                        label="PASS"
                        size="small"
                        sx={{
                          bgcolor: "var(--color-success-bg)",
                          color: T.performed,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          mr: 1,
                        }}
                      />
                    )}
                    {isCompleted && isFailed && (
                      <Chip
                        icon={<CancelIcon sx={{ fontSize: 14 }} />}
                        label="FAIL"
                        size="small"
                        sx={{
                          bgcolor: "var(--color-danger-bg)",
                          color: T.missed,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          mr: 1,
                        }}
                      />
                    )}
                    {!isCompleted && (
                      <Chip
                        label="Pending"
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 700, fontSize: "0.72rem", mr: 1 }}
                      />
                    )}
                    <Chip
                      label="Assess"
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>
    </Box>
  );
}


// ─── Assessment Form ──────────────────────────────────────────────────────────

function AssessmentForm({ courseId, checklistId, studentId }) {
  const navigate = useNavigate();
  const { user } = useContext(TenantContext);

  const [checklist, setChecklist] = useState(null);
  const [student, setStudent] = useState(null);
  const [initialScores, setInitialScores] = useState({});
  const [initialNotes, setInitialNotes] = useState({});
  const [initialMeta, setInitialMeta] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [practiceAttempts, setPracticeAttempts] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [reevaluating, setReevaluating] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [draftSaved, setDraftSaved] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchChecklist(checklistId),
      fetchChecklistEvaluation(courseId, checklistId, studentId),
      fetchUsersByIds([studentId])
        .then((u) => (Array.isArray(u) ? u[0] : null))
        .catch(() => null),
    ]).then(([cl, ev, st]) => {
      setChecklist(cl);
      setStudent(st);
      const meta = { examinerName: user ? (user.fullName || user.username || "") : "", assessmentLocation: "", overridePeerPrereq: false, peerOverrideReason: "", instructorOutcome: "", instructorComment: "" };
      if (ev) {
        setEvaluation(ev);
        const scores = {};
        const notes = {};
        if (Array.isArray(ev.instructorItemResults)) {
          ev.instructorItemResults.forEach((r) => {
            if (r.id != null) {
              const pointsAwarded = r.pointsAwarded == null ? NaN : Number(r.pointsAwarded);
              scores[r.id] =
                Number.isFinite(pointsAwarded) && pointsAwarded > 0
                  ? "performed"
                  : pointsAwarded === 0
                    ? "missed"
                    : null;
              if (typeof r.note === "string") notes[r.id] = r.note;
            }
          });
        }
        setInitialScores(scores);
        setInitialNotes(notes);
        if (ev.examinerName) meta.examinerName = ev.examinerName;
        if (ev.assessmentLocation) meta.assessmentLocation = ev.assessmentLocation;
        if (ev.overridePeerPrereq) meta.overridePeerPrereq = ev.overridePeerPrereq;
        meta.peerOverrideReason = ev.peerOverrideReason || "";
        meta.instructorOutcome = ev.instructorOutcome || "";
        meta.instructorComment = ev.instructorComment || "";
      }
      setInitialMeta(meta);
      setLoading(false);
    }).catch((err) => {
      setError(err?.response?.data?.error || err?.response?.data?.message || "Could not load the assessment. Please retry.");
      setLoading(false);
    });
    fetchPracticeAttempts(checklistId)
      .then((all) => setPracticeAttempts((all || []).filter((a) => String(a.candidateId) === String(studentId))))
      .catch(() => setPracticeAttempts([]));
    Promise.resolve()
      .then(() => fetchEvaluationAttempts(courseId, checklistId, studentId))
      .then((list) => setAttempts(Array.isArray(list) ? list : []))
      .catch(() => setAttempts([]));
  }, [courseId, checklistId, studentId, user, reloadKey]);

  const handleReEvaluate = () => {
    setError(null);
    setReevaluating(true);
    reEvaluateChecklist(courseId, checklistId, studentId)
      .then(() => {
        setReevaluating(false);
        setReloadKey((k) => k + 1);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err?.response?.data?.message || "Could not start a re-evaluation.");
        setReevaluating(false);
      });
  };

  const items = Array.isArray(checklist?.items) ? checklist.items : [];
  const passingScore = checklist?.passingScore ?? null;
  const isLocked = evaluation?.locked ?? false;
  const prereqMet = evaluation?.peerPrereqMet ?? (checklist?.peerAssessmentRequired === false);

  const buildOfficialPayload = (ctx) => ({
    itemResults: items.map((item) => ({
      id: item.id,
      criteria: item.criteria,
      isCritical: item.isCritical ?? false,
      pointValue: itemPoints(item),
      sectionTitle: item.sectionTitle ?? null,
      pointsAwarded:
        ctx.scores[item.id] === "performed"
          ? itemPoints(item)
          : ctx.scores[item.id] === "missed"
            ? 0
            : null,
      note: ctx.notes[item.id] || null,
    })),
    notes: ctx.notes,
    examinerName: (ctx.meta.examinerName || "").trim() || null,
    examinationDate: ctx.examDate || null,
    assessmentLocation: (ctx.meta.assessmentLocation || "").trim() || null,
    overridePeerPrereq: !!ctx.meta.overridePeerPrereq,
    peerOverrideReason: (ctx.meta.peerOverrideReason || "").trim() || null,
    instructorOutcome: ctx.meta.instructorOutcome || null,
    instructorComment: (ctx.meta.instructorComment || "").trim() || null,
  });

  const handleSaveDraft = (ctx) => {
    setSaving(true);
    setError(null);
    setDraftSaved(false);
    saveEvaluationDraft(courseId, checklistId, studentId, buildOfficialPayload(ctx))
      .then((saved) => {
        setEvaluation(saved);
        setDraftSaved(true);
        setSaving(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err?.response?.data?.message || "Failed to save draft.");
        setSaving(false);
      });
  };

  const handleSubmit = (ctx) => {
    setError(null);
    setSubmitting(true);
    submitInstructorSignOff(courseId, checklistId, studentId, buildOfficialPayload(ctx))
      .then((result) => {
        navigate(
          `/course-management/${courseId}/checklists/${checklistId}/results/${studentId}`,
          { state: { result } },
        );
      })
      .catch((err) => {
        const msg =
          err?.response?.status === 409
            ? "This evaluation is already locked."
            : err?.response?.data?.error || err?.response?.data?.message || "Submission failed.";
        setError(msg);
        setSubmitting(false);
      });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!checklist) return <Alert severity="error">{error || "Could not load the assessment. Please retry."}</Alert>;

  const studentName = personName(student) || "Student";
  const studentInitials = studentName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  return (
    <Box
      data-testid="skill-assessment-content"
      sx={{
        bgcolor: T.pageBg, mx: -3, mt: -3, p: { xs: 2, sm: 4 }, pt: 3,
        pb: { xs: "var(--mobile-assessment-footer-clearance)", sm: 16 },
      }}
      style={{ "--mobile-assessment-footer-clearance": MOBILE_ASSESSMENT_FOOTER_CLEARANCE }}
    >
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        {draftSaved && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: "10px" }} onClose={() => setDraftSaved(false)} data-testid="draft-saved">
            Draft saved.
          </Alert>
        )}
        {error && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px" }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLocked && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
            This evaluation has been signed off and locked.{" "}
            <Button
              size="small"
              onClick={() =>
                navigate(
                  `/course-management/${courseId}/checklists/${checklistId}/results/${studentId}`,
                )
              }
            >
              View Results
            </Button>
            {evaluation?.finalResult === "FAIL" && (
              <Button size="small" variant="contained" disabled={reevaluating} onClick={handleReEvaluate} sx={{ ml: 1 }}>
                Start re-evaluation
              </Button>
            )}
          </Alert>
        )}

        {(evaluation?.attemptNumber ?? 1) > 1 || attempts.length > 1 ? (
          <Alert severity="info" icon={false} sx={{ mb: 2, borderRadius: "10px" }} data-testid="evaluation-attempts">
            Attempt {evaluation?.attemptNumber ?? attempts.length}.{" "}
            {attempts.filter((a) => !a.current).map((a) => (
              <Chip
                key={a.attemptNumber}
                size="small"
                sx={{ mr: 0.5 }}
                label={`Attempt ${a.attemptNumber}: ${a.finalResult === "PASS" ? "Pass" : "Fail"}${a.gradePercent != null ? ` (${Math.round(a.gradePercent)}%)` : ""}`}
              />
            ))}
          </Alert>
        ) : null}

        {/* Scenario card */}
        {(checklist?.scenarioContext || checklist?.description) && (
          <Paper
            elevation={0}
            sx={{ borderRadius: "14px", border: `1px solid ${T.border}`, bgcolor: T.surface2, p: 2.5, mb: 2.5, display: "flex", alignItems: "flex-start", gap: 2 }}
          >
            <Box sx={{ width: 36, height: 36, borderRadius: "9px", bgcolor: `${T.primary}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <MonitorHeartIcon sx={{ fontSize: 20, color: T.primary }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: T.na, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                Scenario
              </Typography>
              <Typography sx={{ fontSize: "1rem", color: T.ink, lineHeight: 1.65 }}>
                {checklist.scenarioContext || checklist.description}
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Equipment card */}
        {checklist?.equipment && (
          <Paper
            elevation={0}
            sx={{ borderRadius: "14px", border: `1px solid ${T.border}`, bgcolor: T.surface2, p: 2.5, mb: 2.5, display: "flex", alignItems: "flex-start", gap: 2 }}
          >
            <Box sx={{ width: 36, height: 36, borderRadius: "9px", bgcolor: `${T.primary}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <BuildIcon sx={{ fontSize: 20, color: T.primary }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: T.na, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                Equipment
              </Typography>
              <Typography sx={{ fontSize: "1rem", color: T.ink, lineHeight: 1.65 }}>
                {checklist.equipment}
              </Typography>
            </Box>
          </Paper>
        )}

        {student && (
          <PracticeHistoryPanel
            candidateName={studentName || "This student"}
            attempts={practiceAttempts}
            items={items}
            passingScore={passingScore}
          />
        )}

        <ChecklistAssessmentScreen
          checklist={checklist}
          locked={isLocked}
          initialScores={initialScores}
          initialNotes={initialNotes}
          initialMeta={initialMeta}
          headerRight={() => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, border: "1px solid var(--color-hairline-violet)", borderRadius: "12px", p: "6px 12px 6px 6px" }}>
              <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "var(--color-accent-lime)", color: "var(--color-primary)", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                {studentInitials}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 10.5, color: "var(--color-on-dark-muted) !important", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".12em" }}>Candidate</Typography>
                <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>{studentName}</Typography>
              </Box>
            </Box>
          )}
          footer={(ctx) => {
            const fieldLabelSx = { fontSize: 12, fontWeight: 600, color: "var(--color-ink-2)", mb: "6px" };
            const inputSx = { width: "100%", height: 42, px: "12px", bgcolor: "#fff", border: "1px solid #000", borderRadius: "9px", fontFamily: "inherit", fontSize: 13.5, color: T.ink };
            return (
              <Paper elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.border}`, p: "18px 20px", mb: 2.5 }}>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: T.ink, mb: 2 }}>Assessment information</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "14px" }}>
                  <Box>
                    <Typography sx={fieldLabelSx}>Examiner name</Typography>
                    <Box component="input" value={ctx.meta.examinerName || ""} onChange={(e) => ctx.setMeta({ examinerName: e.target.value })} sx={inputSx} />
                  </Box>
                  <Box>
                    <Typography sx={fieldLabelSx}>Examination date</Typography>
                    <Box component="input" type="date" value={ctx.examDate} onChange={(e) => ctx.setExamDate(e.target.value)} sx={inputSx} />
                  </Box>
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography sx={fieldLabelSx}>Assessment location</Typography>
                    <Box component="input" value={ctx.meta.assessmentLocation || ""} onChange={(e) => ctx.setMeta({ assessmentLocation: e.target.value })} sx={inputSx} />
                  </Box>
                </Box>
                <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
                  <Typography sx={{ fontSize: 13, color: T.na }}>Automatic result: {ctx.r.status === "pass" ? "Pass" : ctx.r.status === "fail" ? "Fail" : "Pending"}. The instructor decision determines the final outcome.</Typography>
                  <TextField select label="Instructor outcome" value={ctx.meta.instructorOutcome || ""} disabled={isLocked} onChange={(e) => ctx.setMeta({ instructorOutcome: e.target.value })} fullWidth size="small">
                    <MenuItem value="">Use automatic result</MenuItem>
                    <MenuItem value="SUCCESSFUL">Successful</MenuItem>
                    <MenuItem value="UNSUCCESSFUL">Unsuccessful</MenuItem>
                  </TextField>
                  <TextField label="Instructor comment" value={ctx.meta.instructorComment || ""} disabled={isLocked} onChange={(e) => ctx.setMeta({ instructorComment: e.target.value })} required={ctx.meta.instructorOutcome === "UNSUCCESSFUL"} error={ctx.meta.instructorOutcome === "UNSUCCESSFUL" && !ctx.meta.instructorComment?.trim()} helperText={ctx.meta.instructorOutcome === "UNSUCCESSFUL" ? "A comment is required for Unsuccessful." : "Optional"} fullWidth multiline minRows={2} />
                  {ctx.meta.overridePeerPrereq && !prereqMet && <TextField label="Peer override reason" value={ctx.meta.peerOverrideReason || ""} onChange={(e) => ctx.setMeta({ peerOverrideReason: e.target.value })} required fullWidth multiline minRows={2} />}
                </Box>
              </Paper>
            );
          }}
          bottomBar={(ctx) => {
            const { r, notes: ctxNotes } = ctx;
            const scoredCount = items.filter((item) => ctx.scores[item.id] != null).length;
            const missedWithoutNote = items.filter((item) => ctx.scores[item.id] === "missed" && !ctxNotes[item.id]?.trim());
            const canSubmit =
              scoredCount === items.length &&
              (prereqMet || (ctx.meta.overridePeerPrereq && Boolean(ctx.meta.peerOverrideReason?.trim()))) &&
              (ctx.meta.instructorOutcome !== "UNSUCCESSFUL" || Boolean(ctx.meta.instructorComment?.trim())) &&
              (ctx.meta.instructorOutcome || passingScore > 0 || r.criticalsFailed.length > 0) &&
              missedWithoutNote.length === 0;
            return (
              <BottomBar
                scoredCount={scoredCount}
                totalCount={items.length}
                earned={r.earned}
                total={r.total}
                status={ctx.meta.instructorOutcome === "SUCCESSFUL" ? "pass" : ctx.meta.instructorOutcome === "UNSUCCESSFUL" ? "fail" : r.status}
                criticalsBlocked={r.criticalsFailed.length > 0}
                missedWithoutNote={missedWithoutNote.length}
                canSubmit={canSubmit}
                override={!!ctx.meta.overridePeerPrereq}
                setOverride={(v) => ctx.setMeta({ overridePeerPrereq: typeof v === "function" ? v(ctx.meta.overridePeerPrereq) : v })}
                prereqMet={prereqMet}
                saving={saving}
                submitting={submitting}
                onSave={() => handleSaveDraft(ctx)}
                onSubmit={() => handleSubmit(ctx)}
                isLocked={isLocked}
              />
            );
          }}
        />
      </Box>
    </Box>
  );
}

// ─── Page Shell ───────────────────────────────────────────────────────────────

export default function SkillChecklistAssessPage() {
  const { id: courseId, checklistId, studentId } = useParams();

  if (studentId) {
    return (
      <AssessmentForm
        courseId={courseId}
        checklistId={checklistId}
        studentId={studentId}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Typography variant="h5" fontWeight={800} color={T.primary} sx={{ mb: 3, fontFamily: "var(--font-display)" }}>
        Select Student to Assess
      </Typography>
      <StudentSelector courseId={courseId} checklistId={checklistId} />
    </Box>
  );
}
